import React from 'react';
import { makeTranslator } from '../i18n';
import '../css/HomeContent.css';

/**
 * Visible, crawlable homepage content rendered below the game on the web.
 *
 * Also baked into build/index.html by scripts/prerender-home.js, so it must
 * render without browser APIs, hooks or context — the caller passes `lang`.
 *
 * Styling deliberately matches the game itself: the example ladder reuses the
 * exact look of the board's `.letter-box` tiles (see GamePage.css), the
 * changed letter is highlighted in the game's "placed letter" green, and the
 * Inter font + theme variables are inherited from the app.
 *
 * Rendered only on the web home route (caller gates with
 * `pathname === '/' && !isNative()`), so it never appears in the native apps.
 */

// Each step changes exactly one letter; `changed` is the index of that letter
// (-1 for the starting word).
const LADDERS = {
    en: [
        { word: 'COLD', changed: -1 },
        { word: 'CORD', changed: 2 },
        { word: 'WORD', changed: 0 },
        { word: 'WARD', changed: 1 },
        { word: 'WARM', changed: 3 },
    ],
    hr: [
        { word: 'KOŽA', changed: -1 },
        { word: 'KOSA', changed: 2 },
        { word: 'KOST', changed: 3 },
        { word: 'POST', changed: 0 },
        { word: 'PRST', changed: 1 },
    ],
    de: [
        { word: 'WALD', changed: -1 },
        { word: 'WAND', changed: 2 },
        { word: 'WIND', changed: 1 },
        { word: 'KIND', changed: 0 },
        { word: 'KINO', changed: 3 },
    ],
    es: [
        { word: 'VASO', changed: -1 },
        { word: 'CASO', changed: 0 },
        { word: 'CASA', changed: 3 },
        { word: 'COSA', changed: 1 },
        { word: 'COPA', changed: 2 },
    ],
    fr: [
        { word: 'TOIT', changed: -1 },
        { word: 'TORT', changed: 2 },
        { word: 'PORT', changed: 0 },
        { word: 'PART', changed: 1 },
        { word: 'PARC', changed: 3 },
    ],
    id: [
        { word: 'HATI', changed: -1 },
        { word: 'HARI', changed: 2 },
        { word: 'JARI', changed: 0 },
        { word: 'JURI', changed: 1 },
        { word: 'JURU', changed: 3 },
    ],
    it: [
        { word: 'MODA', changed: -1 },
        { word: 'CODA', changed: 0 },
        { word: 'COSA', changed: 2 },
        { word: 'CASA', changed: 1 },
        { word: 'CASO', changed: 3 },
    ],
    pl: [
        { word: 'RURA', changed: -1 },
        { word: 'KURA', changed: 0 },
        { word: 'KARA', changed: 1 },
        { word: 'KASA', changed: 2 },
        { word: 'KASK', changed: 3 },
    ],
    ru: [
        { word: 'ПАРК', changed: -1 },
        { word: 'ПАРА', changed: 3 },
        { word: 'ПОРА', changed: 1 },
        { word: 'НОРА', changed: 0 },
        { word: 'НОГА', changed: 2 },
    ],
    pt: [
        { word: 'SAIA', changed: -1 },
        { word: 'SALA', changed: 2 },
        { word: 'BALA', changed: 0 },
        { word: 'BOLA', changed: 1 },
        { word: 'BOLO', changed: 3 },
    ],
    tr: [
        { word: 'BABA', changed: -1 },
        { word: 'KABA', changed: 0 },
        { word: 'KARA', changed: 2 },
        { word: 'KART', changed: 3 },
        { word: 'KURT', changed: 1 },
    ],
};

function LadderWord({ word, changed }) {
    return (
        <div className="home-seo-word">
            {word.split('').map((ch, i) => (
                <span key={i} className={`home-seo-tile${i === changed ? ' changed' : ''}`}>{ch}</span>
            ))}
        </div>
    );
}

const MARKUP = { b: <strong />, i: <em /> };

function HomeContent({ lang = 'en' }) {
    const t = makeTranslator(lang);
    const ladder = LADDERS[lang] || LADDERS.en;
    const faq = (id, open, link) => (
        <details className="home-seo-faq" open={open}>
            <summary>{t(`home.faq.${id}Q`)}</summary>
            <p>{t(`home.faq.${id}A`)}{link && <>{' '}<a href={link}>{t(`home.faq.${id}Link`)}</a></>}</p>
        </details>
    );
    const card = (href, id) => (
        <a className="home-seo-card" href={href}>
            <span className="home-seo-card-t">{t(`home.cards.${id}Title`)}</span>
            <span className="home-seo-card-d">{t(`home.cards.${id}Text`)}</span>
        </a>
    );
    return (
        <section className="home-seo" aria-label={t('home.ariaLabel')}>
            <div className="home-seo-inner">
                <p className="home-seo-eyebrow">{t('home.eyebrow')}</p>
                <h1 className="home-seo-title">{t('home.title')}</h1>
                <p className="home-seo-lead">{t.rich('home.lead', MARKUP)}</p>

                <h2 className="home-seo-h">{t('home.howToPlay')}</h2>
                <div className="home-seo-steps">
                    {['start', 'change', 'reach'].map((id, i) => (
                        <div className="home-seo-step" key={id}>
                            <span className="home-seo-step-n">{i + 1}</span>
                            <h3>{t(`home.steps.${id}Title`)}</h3>
                            <p>{t(`home.steps.${id}Text`)}</p>
                        </div>
                    ))}
                </div>

                <h2 className="home-seo-h">{t('home.seeIt')}</h2>
                <div className="home-seo-example">
                    <p className="home-seo-example-cap">
                        {t.rich('home.exampleCaption', MARKUP, {
                            from: ladder[0].word, to: ladder[ladder.length - 1].word, count: ladder.length - 1,
                        })}
                    </p>
                    <div className="home-seo-ladder">
                        {ladder.map((row, i) => (
                            <React.Fragment key={row.word}>
                                {i > 0 && <span className="home-seo-arrow" aria-hidden="true">▼</span>}
                                <LadderWord word={row.word} changed={row.changed} />
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <h2 className="home-seo-h">{t('home.whatIsHeading')}</h2>
                <p className="home-seo-prose">{t.rich('home.whatIs1', MARKUP)}</p>
                <p className="home-seo-prose">
                    {t('home.whatIs2')}{' '}
                    <a href="/word-ladder/">{t('home.whatIsLink')}</a>
                </p>

                <h2 className="home-seo-h">{t('home.whyHeading')}</h2>
                <p className="home-seo-prose">
                    {t('home.why')}{' '}
                    <a href="/blog/build-vocabulary-with-word-ladders/">{t('home.whyLink')}</a>
                </p>

                <h2 className="home-seo-h">{t('home.tipsHeading')}</h2>
                <ul className="home-seo-prose home-seo-tips">
                    {['bothEnds', 'vowel', 'fixed', 'detour'].map(id => (
                        <li key={id}>{t.rich(`home.tips.${id}`, MARKUP)}</li>
                    ))}
                </ul>

                <h2 className="home-seo-h">{t('home.faqHeading')}</h2>
                {faq('free', true)}
                {faq('length')}
                {faq('wordle')}
                {faq('solvable', false, '/blog/why-some-word-ladders-are-impossible/')}
                {faq('hint')}
                {faq('languages')}

                <h2 className="home-seo-h">{t('home.exploreHeading')}</h2>
                {lang !== 'en' && <p className="home-seo-prose">{t('home.exploreNote')}</p>}
                <nav className="home-seo-links" aria-label={t('home.exploreHeading')}>
                    {card('/how-to-play/', 'howToPlay')}
                    {card('/word-ladder/', 'guide')}
                    {card('/puzzles/', 'archive')}
                    {card('/blog/', 'blog')}
                    {card('/faq/', 'faq')}
                    {card('/about/', 'about')}
                </nav>
            </div>
        </section>
    );
}

export default HomeContent;
